import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldCheck, Plus, Pencil, Trash2, Calculator, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const AUTH_KEY = "eviq_admin_token";

const authHeaders = () => {
  const t = localStorage.getItem(AUTH_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_TONES = {
  HEALTHY: "#39FF14",
  WATCH: "#FFB020",
  "AT RISK": "#FF6B57",
  EXPIRED: "#71717A",
  COVERED: "#00E5FF",
};

const EMPTY_FORM = {
  manufacturer: "", model: "", model_year_start: "", model_year_end: "",
  warranty_years: "", warranty_miles: "", capacity_threshold_percent: "",
  battery_capacity_kwh: "", epa_range_new: "", market: "US",
  effective_date: "", source: "", source_url: "", verified_date: "",
};

/** AdminWarrantyDB — /admin/warranty
 *  EV_BATTERY_WARRANTY: EVIQ's own warranty-rules database (no OEM API
 *  dependency) + a live warranty-position calculator that combines a rule
 *  with vehicle telemetry inputs. */
export default function AdminWarrantyDB() {
  const [authed, setAuthed] = useState(null);
  const [rules, setRules] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/admin/me`, { credentials: "include", headers: authHeaders() })
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await fetch(`${API}/api/warranty-db/rules`, { credentials: "include", headers: authHeaders() });
      if (r.status === 401) { setAuthed(false); return; }
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      setRules((await r.json()).rules || []);
    } catch (e) {
      setError(e.message || "Failed to load warranty rules");
    }
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const startEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      ...EMPTY_FORM,
      ...Object.fromEntries(Object.entries(rule).filter(([k]) => k in EMPTY_FORM)
        .map(([k, v]) => [k, v == null ? "" : String(v)])),
    });
    setFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const body = {
      manufacturer: form.manufacturer.trim(),
      model: form.model.trim(),
      model_year_start: +form.model_year_start,
      model_year_end: +form.model_year_end,
      warranty_years: +form.warranty_years,
      warranty_miles: form.warranty_miles === "" ? null : +form.warranty_miles,
      capacity_threshold_percent: form.capacity_threshold_percent === "" ? null : +form.capacity_threshold_percent,
      battery_capacity_kwh: form.battery_capacity_kwh === "" ? null : +form.battery_capacity_kwh,
      epa_range_new: form.epa_range_new === "" ? null : +form.epa_range_new,
      market: form.market.trim() || "US",
      effective_date: form.effective_date || null,
      source: form.source || null,
      source_url: form.source_url || null,
      verified_date: form.verified_date || null,
    };
    try {
      const url = editingId ? `${API}/api/warranty-db/rules/${editingId}` : `${API}/api/warranty-db/rules`;
      const r = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail?.[0]?.msg || j.detail || "Save failed");
      setForm(EMPTY_FORM);
      setEditingId(null);
      setFormOpen(false);
      await load();
    } catch (e2) {
      setError(typeof e2.message === "string" ? e2.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule: ${rule.manufacturer} ${rule.model} (${rule.model_year_start}–${rule.model_year_end})?`)) return;
    await fetch(`${API}/api/warranty-db/rules/${rule.id}`, {
      method: "DELETE", credentials: "include", headers: authHeaders(),
    });
    await load();
  };

  if (authed === false) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-sm text-zinc-300 mb-3">Admin sign-in required.</div>
          <Link to="/admin" className="text-[11px] font-mono uppercase tracking-wider text-[#39FF14]">← Go to admin login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-warranty-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/admin" className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white inline-flex items-center gap-1.5 mb-5" data-testid="warranty-db-back">
          <ArrowLeft className="w-3 h-3" strokeWidth={2.4} /> Admin dashboard
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-1 inline-flex items-center gap-2">
              <ShieldCheck className="w-3 h-3" style={{ color: "#39FF14" }} strokeWidth={2.4} />
              EV_BATTERY_WARRANTY
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
              Warranty rules <span className="brand-text-gradient">database</span>
            </h1>
            <p className="text-xs text-zinc-400 mt-1 max-w-xl leading-relaxed">
              EVIQ-curated warranty terms — an owned dataset, not an OEM API dependency.
              Telemetry + these rules = the warranty position shown across the app.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="warranty-db-refresh"
              className="px-3 py-2 rounded-lg border border-white/10 hover:bg-white/5 text-[11px] font-mono uppercase tracking-wider text-zinc-300 inline-flex items-center gap-1.5">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
            <button
              onClick={() => { setFormOpen((v) => !v); setEditingId(null); setForm(EMPTY_FORM); }}
              data-testid="warranty-db-add-btn"
              className="brand-gradient text-black font-semibold px-3 py-2 rounded-lg text-[11px] font-mono uppercase tracking-wider inline-flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" strokeWidth={2.6} /> Add rule
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-[#FF6B57]/30 bg-[#FF6B57]/[0.06] px-4 py-3 text-sm text-[#FF6B57] mb-4" data-testid="warranty-db-error">
            {error}
          </div>
        )}

        {formOpen && (
          <form onSubmit={submit} className="rounded-2xl border border-[#00E5FF]/25 bg-[#00E5FF]/[0.03] p-4 sm:p-5 mb-5" data-testid="warranty-db-form">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00E5FF] mb-3">
              {editingId ? "Edit rule" : "New rule"}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ["manufacturer", "Manufacturer *", "Tesla"],
                ["model", "Model *", "Model 3 Long Range"],
                ["model_year_start", "Year start *", "2017"],
                ["model_year_end", "Year end *", "2026"],
                ["warranty_years", "Warranty years *", "8"],
                ["warranty_miles", "Warranty miles", "120000"],
                ["capacity_threshold_percent", "Capacity floor %", "70"],
                ["battery_capacity_kwh", "Pack kWh", "82"],
                ["epa_range_new", "EPA range new", "333"],
                ["market", "Market", "US"],
                ["effective_date", "Effective date", "2017-01-01"],
                ["verified_date", "Verified date", "2026-06-01"],
                ["source", "Source", "Tesla NVLW"],
                ["source_url", "Source URL", "https://…"],
              ].map(([key, label, ph]) => (
                <label key={key} className="block">
                  <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">{label}</span>
                  <input
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    placeholder={ph}
                    data-testid={`warranty-form-${key}`}
                    className="w-full rounded-lg border border-white/10 bg-black/60 px-2.5 py-2 text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-[#00E5FF]/50"
                  />
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button type="submit" disabled={saving} data-testid="warranty-form-save"
                className="brand-gradient text-black font-semibold px-4 py-2 rounded-lg text-xs font-mono uppercase tracking-wider disabled:opacity-50">
                {saving ? "Saving…" : editingId ? "Update rule" : "Create rule"}
              </button>
              <button type="button" onClick={() => { setFormOpen(false); setEditingId(null); setForm(EMPTY_FORM); }}
                className="px-4 py-2 rounded-lg border border-white/10 text-xs font-mono uppercase tracking-wider text-zinc-400">
                Cancel
              </button>
            </div>
          </form>
        )}

        <PositionCalculator rules={rules || []} />

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto" data-testid="warranty-db-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10">
                {["Manufacturer", "Model", "Years", "Warranty", "Floor", "Pack", "EPA", "Market", "Source", "Verified", ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-mono text-[9px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rules === null ? (
                <tr><td colSpan={11} className="px-3 py-6 text-center text-zinc-500 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading rules…
                </td></tr>
              ) : rules.length === 0 ? (
                <tr><td colSpan={11} className="px-3 py-6 text-center text-zinc-500 text-xs">No rules yet — add one above.</td></tr>
              ) : rules.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`warranty-rule-${r.id}`}>
                  <td className="px-3 py-2.5 text-xs text-white whitespace-nowrap">{r.manufacturer}</td>
                  <td className="px-3 py-2.5 text-xs text-zinc-200">{r.model}</td>
                  <td className="px-3 py-2.5 text-xs font-mono text-zinc-400 whitespace-nowrap">{r.model_year_start}–{r.model_year_end}</td>
                  <td className="px-3 py-2.5 text-xs font-mono text-white whitespace-nowrap">
                    {r.warranty_years}yr / {r.warranty_miles ? `${(r.warranty_miles / 1000).toFixed(0)}k mi` : "unltd"}
                  </td>
                  <td className="px-3 py-2.5 text-xs font-mono whitespace-nowrap" style={{ color: r.capacity_threshold_percent ? "#FFB020" : "#71717A" }}>
                    {r.capacity_threshold_percent ? `${r.capacity_threshold_percent}%` : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-xs font-mono text-zinc-400 whitespace-nowrap">{r.battery_capacity_kwh ? `${r.battery_capacity_kwh} kWh` : "—"}</td>
                  <td className="px-3 py-2.5 text-xs font-mono text-zinc-400 whitespace-nowrap">{r.epa_range_new ? `${r.epa_range_new} mi` : "—"}</td>
                  <td className="px-3 py-2.5 text-xs font-mono text-zinc-500">{r.market}</td>
                  <td className="px-3 py-2.5 text-[11px] text-zinc-400 max-w-[180px]">
                    {r.source_url ? <a href={r.source_url} target="_blank" rel="noopener noreferrer" className="hover:text-white underline decoration-white/20">{r.source || "source"}</a> : (r.source || "—")}
                  </td>
                  <td className="px-3 py-2.5 text-xs font-mono text-zinc-500 whitespace-nowrap">{r.verified_date || "—"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <button onClick={() => startEdit(r)} data-testid={`warranty-edit-${r.id}`} aria-label="Edit rule"
                      className="p-1.5 rounded border border-white/10 text-zinc-400 hover:text-white mr-1.5">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button onClick={() => remove(r)} data-testid={`warranty-delete-${r.id}`} aria-label="Delete rule"
                      className="p-1.5 rounded border border-[#FF6B57]/30 text-[#FF6B57]/70 hover:text-[#FF6B57]">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Combines a warranty rule with telemetry inputs → warranty position. */
const PositionCalculator = ({ rules }) => {
  const [ruleId, setRuleId] = useState("");
  const [year, setYear] = useState("2024");
  const [age, setAge] = useState("3.2");
  const [miles, setMiles] = useState("47840");
  const [soh, setSoh] = useState("82");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const compute = async (e) => {
    e.preventDefault();
    const rule = rules.find((r) => r.id === ruleId);
    if (!rule) { setErr("Pick a vehicle rule first."); return; }
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const r = await fetch(`${API}/api/warranty-db/position`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          manufacturer: rule.manufacturer,
          model: rule.model,
          model_year: +year,
          age_years: +age,
          odometer_mi: +miles,
          soh_pct: +soh,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || "Calculation failed");
      setResult(j.position);
    } catch (e2) {
      setErr(e2.message || "Calculation failed");
    } finally {
      setBusy(false);
    }
  };

  const tone = result ? (STATUS_TONES[result.status] || "#00E5FF") : "#00E5FF";

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5 mb-5" data-testid="warranty-calculator">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3 inline-flex items-center gap-2">
        <Calculator className="w-3 h-3" style={{ color: "#39FF14" }} strokeWidth={2.4} />
        Warranty position calculator · telemetry × rules
      </div>
      <form onSubmit={compute} className="grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
        <label className="col-span-2 block">
          <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">Vehicle rule</span>
          <select value={ruleId} onChange={(e) => setRuleId(e.target.value)} data-testid="calc-rule-select"
            className="w-full rounded-lg border border-white/10 bg-black/60 px-2.5 py-2 text-xs text-white focus:outline-none focus:border-[#39FF14]/50">
            <option value="">Select…</option>
            {rules.map((r) => (
              <option key={r.id} value={r.id}>{r.manufacturer} {r.model} ({r.model_year_start}–{r.model_year_end})</option>
            ))}
          </select>
        </label>
        {[["Model year", year, setYear, "calc-year"], ["Age (years)", age, setAge, "calc-age"],
          ["Odometer (mi)", miles, setMiles, "calc-miles"], ["EVIQ Estimated SoH %", soh, setSoh, "calc-soh"]].map(([label, val, set, tid]) => (
          <label key={tid} className="block">
            <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 block mb-1">{label}</span>
            <input value={val} onChange={(e) => set(e.target.value)} data-testid={tid}
              className="w-full rounded-lg border border-white/10 bg-black/60 px-2.5 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#39FF14]/50" />
          </label>
        ))}
        <button type="submit" disabled={busy} data-testid="calc-submit"
          className="brand-gradient text-black font-semibold px-3 py-2 rounded-lg text-[11px] font-mono uppercase tracking-wider disabled:opacity-50">
          {busy ? "…" : "Compute"}
        </button>
      </form>
      {err && <div className="text-[11px] text-[#FF6B57] font-mono mt-2" data-testid="calc-error">{err}</div>}
      {result && (
        <div className="mt-4 rounded-xl border p-4" style={{ borderColor: `${tone}44`, background: `${tone}0A` }} data-testid="calc-result">
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-400">EVIQ Warranty Status:</span>
            <span className="font-mono text-sm font-bold px-2 py-0.5 rounded" style={{ color: tone, background: `${tone}18`, border: `1px solid ${tone}55` }} data-testid="calc-status">
              {result.status}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 font-mono text-xs">
            <ResultRow label="Battery SoH" value={`${result.soh_pct}%`} />
            <ResultRow label="Warranty threshold" value={result.warranty_threshold_pct != null ? `${result.warranty_threshold_pct}%` : "none (defects only)"} />
            <ResultRow label="Margin above threshold" value={result.margin_pct_points != null ? `${result.margin_pct_points} pts` : "—"} tone={tone} />
            <ResultRow label="Mileage remaining" value={result.miles_remaining != null ? `${result.miles_remaining.toLocaleString()} mi` : "unlimited"} />
            <ResultRow label="Time remaining" value={`${result.years_remaining} years`} />
          </div>
        </div>
      )}
    </div>
  );
};

const ResultRow = ({ label, value, tone }) => (
  <div>
    <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
    <div className="text-white text-sm" style={tone ? { color: tone } : undefined}>{value}</div>
  </div>
);
